'use client'

import { GradientBadge } from '@/ui/atoms/badge/Badge'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { ArrowRight } from 'lucide-react'

export type OfferingSize = 'large' | 'featured' | 'medium' | 'cta'
interface OfferingCardProps {
  title: string
  description: string
  size: OfferingSize
  withNewBadge?: boolean
  index: number
  onClick: () => void
  children?: React.ReactNode
}

export function OfferingCard({ title, description, size, withNewBadge, index, onClick, children }: OfferingCardProps) {
  const handleCardClick = () => {
    if (size === 'cta' || window.matchMedia('(min-width: 768px)').matches) {
      onClick()
    }
  }

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onClick()
  }

  return (
    <div
      className={cn()}
      style={{
        animationDelay: `${index * 0.08}s`,
        animationName: 'sweep-card-in',
        animationDuration: '0.4s',
        animationTimingFunction: 'ease-out',
        animationFillMode: 'forwards',
        opacity: 0,
      }}
    >
      <div
        onClick={handleCardClick}
        className={cn(
          'group relative h-full rounded-2xl bg-gradient-to-r from-gradient-yellow/20 to-gradient-green/20 p-[1px] transition-all hover:from-gradient-yellow/40 hover:to-gradient-green/40',
          size !== 'cta' && 'md:cursor-pointer',
          size === 'cta' && 'cursor-pointer',
        )}
      >
        <div
          className={cn(
            'flex h-full flex-col justify-between rounded-2xl bg-card p-5',
            size === 'featured' && 'min-h-[220px] sm:min-h-[320px]',
            size === 'large' && 'min-h-[180px] sm:min-h-[200px]',
            size === 'medium' && 'min-h-[140px] sm:min-h-[160px]',
            size === 'cta' && 'items-center bg-gradient-to-br from-40% from-card to-gradient-green/10 text-center',
          )}
        >
          <div className={cn('flex items-start justify-between gap-3', size === 'cta' && 'flex-col items-center')}>
            <div className={cn('flex flex-col gap-1', size === 'cta' && 'items-center')}>
              <div className="flex items-center gap-2">
                <Typography
                  variant="h3"
                  className={cn('font-bold text-white', {
                    'text-xl sm:text-2xl': size === 'large' || size === 'featured',
                    'text-xl': size === 'medium',
                  })}
                  gradient
                  style={size === 'cta' ? { alignSelf: 'center' } : undefined}
                >
                  {title}
                </Typography>

                {withNewBadge && (
                  <GradientBadge>
                    <Typography variant="prompt" gradient>
                      New!
                    </Typography>
                  </GradientBadge>
                )}
              </div>

              <p className="max-w-md text-muted-foreground text-sm leading-relaxed">{description}</p>
            </div>

            {size !== 'cta' && (
              <button
                type="button"
                onClick={handleButtonClick}
                className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform sm:h-10 sm:w-10 hover:scale-110"
              >
                <ArrowRight className="h-4 w-4 text-black sm:h-5 sm:w-5" />
              </button>
            )}
          </div>

          {children && <div className={cn('mt-4', size === 'featured' && 'mt-8')}>{children}</div>}
        </div>
      </div>
    </div>
  )
}
